import React from 'react';
import { backdropCloseHandlers } from '../../../../utils/backdropClose';
import orthoMapsExample from '../../../../AssetsMaps/maps_wide.png';

/** Explains what ortho maps are and why lanes must be traced on them, not on the Google Maps base layer. */
function OrthoMapsInfoModal({ open, onClose }) {
  if (!open) return null;
  return (
    <div
      className="TreeModalOverlay ortho-maps-info-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ortho-maps-info-title"
      {...backdropCloseHandlers(onClose)}
    >
      <div
        className="TreeModalContainer ortho-maps-info-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header ortho-maps-info-modal-header">
          <h2 className="modal-title ortho-maps-info-modal-title" id="ortho-maps-info-title">
            <i className="fa-solid fa-circle-info ortho-maps-info-modal-title-icon" aria-hidden />
            What is an Ortho Map?
          </h2>
        </div>
        <div className="modal-content ortho-maps-info-modal-body">
          <p className="ortho-maps-info-intro">
            An <strong>ortho map</strong> is a top-down PNG image generated directly from the{' '}
            <strong>LiDAR (LAS) scan</strong> of this road — the same point cloud data the RSA
            analysis runs on. Every pixel in it is an accurate, to-scale representation of what
            was actually surveyed.
          </p>
          <figure className="ortho-maps-info-figure">
            <img
              src={orthoMapsExample}
              alt="Ortho map tiles overlaid on Google Maps, showing sharp gray LiDAR-derived tiles following the road while the surrounding trails and lake come from Google Maps"
              className="ortho-maps-info-figure-img"
            />
            <figcaption className="ortho-maps-info-figure-caption">
              The gray tiles are the ortho map — generated from the LAS scan and laid directly
              over the road. Everything outside them (the lake, trails, street names) is Google
              Maps, shown only for context.
            </figcaption>
          </figure>
          <p className="ortho-maps-info-subhead">Why you need it</p>
          <p>
            Lanes must be traced on top of the ortho map, not the Google Maps base layer
            underneath it. The analysis measures sight distance, geometry, and markings against
            the LAS data — so a lane that looks right on Google Maps but doesn't match the ortho
            map will be wrong in the actual analysis.
          </p>
          <p className="ortho-maps-info-subhead">Google Maps vs. the ortho map</p>
          <ul className="ortho-maps-info-list">
            <li>
              Google's satellite/street imagery can be <strong>lower resolution</strong> than the
              LiDAR scan, especially for lane markings and shoulder edges.
            </li>
            <li>
              It can also be <strong>out of date</strong> — showing an old road layout, a
              since-removed lane, or construction that happened after the imagery was captured.
            </li>
            <li>
              The ortho map reflects the road <strong>exactly as it was when scanned</strong>,
              which is what the analysis actually measures.
            </li>
          </ul>
          <p className="ortho-maps-info-tip">
            Rule of thumb: once the ortho map is loaded, trace against it — treat Google Maps as
            background context only, not a source of truth for lane placement.
          </p>
        </div>
        <div className="modal-actions">
          <button type="button" className="modal-btn modal-btn-primary" onClick={onClose}>
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}

export default OrthoMapsInfoModal;
